/* /assets/os-bis.js
 *
 * OS CUSTOM — Back-in-stock, migrated to Klaviyo's current CLIENT API.
 * See docs/BIS-MIGRATION-KLAVIYO-CLIENT-API.md for the full root-cause
 * writeup and spec references; this is the implementation.
 *
 * WHY: the legacy endpoint the notify form used to post to
 * (https://a.klaviyo.com/onsite/components/back-in-stock/subscribe,
 * assets/onsite.js's own internal jQuery-clone AJAX layer) now returns
 * HTTP 410 Gone — Klaviyo retired it. Vendor-side, not a theme bug; the
 * fix is to repoint the actual network call at Klaviyo's current
 * endpoint (verified against the authoritative OpenAPI spec,
 * create_client_back_in_stock_subscription.json, 28 Jul 2026):
 *
 *   POST https://a.klaviyo.com/client/back-in-stock-subscriptions?company_id={PUBLIC_KEY}
 *   Headers: revision: 2026-07-15 (current default per the spec — the
 *            migration doc's draft guessed 2026-04-15; the fetched spec
 *            corrects that), Content-Type: application/vnd.api+json
 *   Success: 202 Accepted
 *
 * company_id = the store's PUBLIC API key/Site ID (settings.klaviyo_
 * public_api_key), already exposed in page source via the Klaviyo web
 * pixel. Public key only, as required for this client-side endpoint —
 * never a private key here.
 *
 * SHARED COMPONENT — this file is byte-identical between the OS and FB
 * repos. Each brand has its own Klaviyo account, so COMPANY_ID is read
 * from window.osBisSettings.companyId (set inline in os-body.liquid
 * from the theme setting) rather than hardcoded — one component,
 * brand-configured, not two copies. Any change here should be applied
 * to both repos.
 *
 * ⚠️ CATALOG-SYNC PREREQUISITE — UNCONFIRMED as of this commit. The
 * client BIS endpoint subscribes a profile against a catalog-variant
 * relationship ($shopify:::$default:::{shopify_variant_id} — confirmed
 * as the correct Shopify-integration id format via the OpenAPI spec's
 * own example, not just the migration doc's assumption). If OS's
 * Shopify catalog isn't actually synced into Klaviyo, every subscribe
 * call below will fail regardless of how correct this code is. DO NOT
 * treat a successful preview-theme submit alone as proof this works —
 * confirm the subscription actually lands in Klaviyo (profile shows a
 * back-in-stock subscription) before removing the interim safety
 * measure below or calling this migration done.
 *
 * INTERCEPT STRATEGY: onsite.js is a vendor file — "touch no stock
 * Pipeline file" extends to it by established convention, and there's
 * no public API to unbind its own jQuery-clone submit handler from the
 * outside. Rather than fight a timing race to get MY handler in before
 * onsite.js's own (attached directly to the form, at some point during
 * an async product-JSON fetch I don't control the timing of), this
 * exploits a DOM-spec guarantee that doesn't depend on timing at all:
 * a CAPTURE-phase listener on an ANCESTOR (the iframe's document)
 * always runs before a bubble-phase listener on the target itself
 * (the form) — capture phase completes before the event ever reaches
 * AT_TARGET, unconditionally, regardless of when either listener was
 * attached. Calling stopPropagation() during capture halts the event
 * entirely, so onsite.js's own submit handler — and thus its dead POST
 * to the legacy endpoint — never runs at all. Nothing in onsite.js
 * itself is modified; only ITS event never reaches its own listener.
 *
 * Everything else about onsite.js's role is untouched: it still
 * injects the .klaviyo-bis-trigger anchor and renders the modal iframe
 * (back_in_stock_document.html) — this only takes over what happens
 * when that modal's form is submitted.
 *
 * showSuccess()/showError() reveal and restyle #completed_message/
 * #error_message (the template's own elements, back_in_stock_document.
 * html) as an on-brand charcoal headline+sub pair instead of Klaviyo's
 * default green/red alert boxes - see their own comments for why
 * #completed_message specifically preserves its original close-link
 * element rather than rebuilding it.
 */
(function () {
  'use strict';

  var IFRAME_ID = 'klaviyo-bis-iframe';
  var CLIENT_ENDPOINT = 'https://a.klaviyo.com/client/back-in-stock-subscriptions';
  var COMPANY_ID = (window.osBisSettings && window.osBisSettings.companyId) || '';
  var REVISION = '2026-07-15';
  var CATALOG_INTEGRATION_PREFIX = '$shopify:::$default:::';
  var EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  var SUCCESS_HEADLINE = "You're on the list.";
  var SUCCESS_SUB = "If this item comes back into stock, you'll be the first to know.";
  var ERROR_HEADLINE = 'Something went wrong.';
  var ERROR_SUB = "Reload the page and try again — or get in touch if it keeps happening and we'll sort it.";

  // Shared headline/sub-line styling - on-brand charcoal/muted-grey, no
  // green/red. Deliberately no font-family here: text inherits the
  // modal's existing Univers Next (#klaviyo-bis-modal * { font-family },
  // klaviyo.liquid) rather than setting a different one.
  var HEADLINE_STYLE = 'color:#2D2D2C;font-size:17px;font-weight:500;letter-spacing:.01em;margin:0 0 8px;';
  var SUB_STYLE = 'color:#6C6C6B;font-size:14px;line-height:1.6;margin:0;';

  function getModalDocument() {
    var iframe = document.getElementById(IFRAME_ID);
    if (!iframe) return null;
    try {
      return iframe.contentDocument || (iframe.contentWindow && iframe.contentWindow.document) || null;
    } catch (e) {
      return null;
    }
  }

  function buildLine(doc, tag, text, cssText) {
    var el = doc.createElement(tag);
    el.textContent = text;
    el.style.cssText = cssText;
    return el;
  }

  function showSuccess() {
    var doc = getModalDocument();
    if (!doc) return;
    var form = doc.querySelector('form');
    var completed = doc.getElementById('completed_message');
    if (!completed) return;

    if (form) {
      Array.prototype.forEach.call(form.children, function (child) {
        if (child !== completed) child.style.display = 'none';
      });
    }

    // Override the small default .alert-success treatment (green text,
    // tinted background, 2px radius) with a prominent, on-brand block -
    // sharp corners, generous padding, no colour signal at all (success
    // is quiet - the red accent below is reserved for error only).
    completed.style.cssText =
      'display:block;' +
      'background:none;' +
      'border:1px solid #E5E6E6;' +
      'border-radius:0;' +
      'padding:24px 22px;' +
      'margin:20px 0 0;';

    // CRITICAL: the template's original close link
    // (<a class="klaviyo-bis-close">, back_in_stock_document.html) is
    // what onsite.js's body-level delegated click listener recognises -
    // it keys off the .klaviyo-bis-close CLASS at click time, not a
    // specific element reference, but re-using the SAME node (removed
    // from the DOM then re-appended, never destroyed/recreated via
    // innerHTML) is the conservative choice that's guaranteed not to
    // lose it, rather than relying on that delegation detail holding.
    var closeLink = completed.querySelector('a.klaviyo-bis-close');
    Array.prototype.slice.call(completed.childNodes).forEach(function (node) {
      if (node !== closeLink) completed.removeChild(node);
    });

    var headline = buildLine(doc, 'p', SUCCESS_HEADLINE, HEADLINE_STYLE);
    var sub = buildLine(doc, 'p', SUCCESS_SUB, SUB_STYLE);
    completed.insertBefore(headline, closeLink || null);
    completed.insertBefore(sub, closeLink || null);

    if (closeLink) {
      closeLink.style.cssText =
        'display:inline-block;' +
        'color:#2D2D2C;' +
        'font-size:13px;' +
        'border-bottom:1px solid #2D2D2C;' +
        'padding-bottom:2px;' +
        'margin-top:18px;';
    }
  }

  function showError() {
    var doc = getModalDocument();
    if (!doc) return;
    var errorEl = doc.getElementById('error_message');
    if (errorEl) {
      // #error_message has no close link in the template (unlike
      // #completed_message) - safe to clear and rebuild freely.
      errorEl.textContent = '';
      errorEl.style.cssText =
        'display:block;' +
        'background:none;' +
        'border:1px solid #E5E6E6;' +
        'border-top:2px solid #AB2328;' + // the one restrained brand-red signal
        'border-radius:0;' +
        'padding:24px 22px;' +
        'margin:16px 0 0;';
      errorEl.appendChild(buildLine(doc, 'p', ERROR_HEADLINE, HEADLINE_STYLE));
      errorEl.appendChild(buildLine(doc, 'p', ERROR_SUB, SUB_STYLE));
    }
    var button = doc.querySelector('form button[type="submit"]');
    if (button) button.removeAttribute('disabled');
  }

  function getCurrentVariantId() {
    // Per the migration doc: source the variant from the product form's
    // own hidden input, not the modal's #variants <select> - the form's
    // value is kept live-current by os-product-form.js on every variant
    // change (including the load-pass fix that defaults to the first
    // AVAILABLE variant), whereas the modal was rendered once and could
    // be stale if the shopper changed size after opening it.
    var input = document.querySelector('[data-product-form] input[name="id"]');
    return input ? input.value : null;
  }

  function submitSubscription(doc, form) {
    var emailInput = doc.getElementById('email');
    var email = emailInput ? emailInput.value.trim() : '';
    var variantId = getCurrentVariantId();

    if (!EMAIL_PATTERN.test(email)) {
      // showError() no longer takes a custom message (the redesigned
      // error state is a fixed headline+sub pair) - falls back to the
      // generic copy here too rather than reintroducing a bespoke
      // message for this one case.
      showError();
      return;
    }
    if (!variantId) {
      showError();
      return;
    }

    var submitButton = form.querySelector('button[type="submit"]');
    if (submitButton) submitButton.setAttribute('disabled', 'disabled');

    var body = {
      data: {
        type: 'back-in-stock-subscription',
        attributes: {
          channels: ['EMAIL'],
          profile: {
            data: {
              type: 'profile',
              attributes: { email: email }
            }
          }
        },
        relationships: {
          variant: {
            data: {
              type: 'catalog-variant',
              id: CATALOG_INTEGRATION_PREFIX + variantId
            }
          }
        }
      }
    };

    fetch(CLIENT_ENDPOINT + '?company_id=' + COMPANY_ID, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/vnd.api+json',
        revision: REVISION
      },
      body: JSON.stringify(body)
    })
      .then(function (response) {
        if (response.status === 202) {
          showSuccess();
        } else {
          showError();
        }
      })
      .catch(function () {
        showError();
      });
  }

  function handleCapturedSubmit(event) {
    var form = event.target;
    if (!form || form.tagName !== 'FORM') return;
    // Halts propagation during the capture phase, before the event ever
    // reaches the form itself - onsite.js's own bubble-phase submit
    // handler (bound directly to this form) never runs, so its dead
    // POST to the legacy endpoint never fires either. See file header.
    event.preventDefault();
    event.stopPropagation();

    var doc = form.ownerDocument;
    submitSubscription(doc, form);
  }

  function bindToIframe(iframe) {
    if (!iframe || iframe.__osBisBound) return;
    var doc;
    try {
      doc = iframe.contentDocument || (iframe.contentWindow && iframe.contentWindow.document);
    } catch (e) {
      return;
    }
    if (!doc) return;
    iframe.__osBisBound = true;
    doc.addEventListener('submit', handleCapturedSubmit, true);
    // Belt-and-braces: onsite.js writes the form via document.write()
    // asynchronously (on first trigger click, not at page load), which
    // per spec may or may not preserve listeners already attached to
    // the document depending on browser/timing. Re-binding (idempotent -
    // addEventListener no-ops for an identical listener+options pair
    // already registered) on the iframe's own load event, which fires
    // after document.write()+close() finishes, is a safe backstop.
    iframe.addEventListener('load', function () {
      doc.addEventListener('submit', handleCapturedSubmit, true);
    });
  }

  function findAndBind() {
    var existing = document.getElementById(IFRAME_ID);
    if (existing) {
      bindToIframe(existing);
      return true;
    }
    return false;
  }

  if (!findAndBind()) {
    var observer = new MutationObserver(function () {
      if (findAndBind()) observer.disconnect();
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }
})();
